import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import {
  CodeMirrorEditor as Editor,
  CodeMirrorEditor as CodeMirrorEditorType,
} from './CodeMirrorEditor';
import { useDebounce } from '../helpers/useDebounce';
import { useUpdateFile } from '../helpers/useFilesApi';
import { usePresenceHeartbeat } from '../helpers/usePresenceApi';
import { useThemeMode } from '../helpers/themeMode';
import { Selectable } from 'kysely';
import { SessionFiles } from '../helpers/schema';
import { User } from '../helpers/User';
import { toast } from 'sonner';
import { CollaboratorCursors } from './CollaboratorCursors';
import { UserAvatar } from './UserAvatar';
import { Input } from './Input';
import { Search, Pencil } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from './Tooltip';
import { Button } from './Button';
import { buildSymbolIndex, type SymbolIndex, type FileInput } from '../lib/symbolIndex';
import { createLspExtensions, type LspConfig } from '../lib/editorLspExtensions';
import styles from './CodeEditor.module.css';

interface CodeEditorProps {
  file: Selectable<SessionFiles>;
  sessionId: string;
  currentUser: User;
  sessionFiles?: Selectable<SessionFiles>[]; // All files for page selector
  selectedPage?: string; // Selected HTML page for preview
  onPageChange?: (pagePath: string) => void; // Callback when page is selected
  onAnnotate?: (dataUrl: string) => void;
}

export const CodeEditor = ({ file, sessionId, currentUser, sessionFiles, onAnnotate }: CodeEditorProps) => {
  const fileId = String(file.id);

  const [content, setContent] = useState(file.content || '');
  const debouncedContent = useDebounce(content, 1000);
  const updateFile = useUpdateFile();
  const [searchQuery, setSearchQuery] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);
  const { effectiveTheme } = useThemeMode();

  const monacoTheme = effectiveTheme === 'light' ? 'tasking-light' : 'tasking-dark';

  // ── LSP: Build symbol index from all session files ──
  const symbolIndexRef = useRef<SymbolIndex | null>(null);

  const symbolIndex = useMemo(() => {
    if (!sessionFiles?.length) return null;
    const inputs: FileInput[] = sessionFiles.map((f) => ({
      fileName: f.fileName,
      filePath: f.filePath || `/${f.fileName}`,
      content: f.id === file.id ? content : (f.content ?? ''),
    }));
    const idx = buildSymbolIndex(inputs);
    symbolIndexRef.current = idx;
    return idx;
  }, [sessionFiles, file.id, content]);

  // LSP extensions for go-to-definition, hover, linting
  const lspExtensions = useMemo(() => {
    const filePath = file.filePath || `/${file.fileName}`;
    const config: LspConfig = {
      fileName: file.fileName,
      filePath,
      getIndex: () => symbolIndexRef.current,
      getFileInput: () => ({
        fileName: file.fileName,
        filePath,
        content,
      }),
      onNavigate: (targetPath, line, _col) => {
        // Dispatch custom event for file navigation
        window.dispatchEvent(
          new CustomEvent('tasking.navigateToFile', {
            detail: { filePath: targetPath, line },
          }),
        );
      },
    };
    return createLspExtensions(config);
  }, [file.fileName, file.filePath, content]);

  const editorRef = useRef<CodeMirrorEditorType | null>(null);
  const lastEditFileIdRef = useRef<string | null>(null);
  const prevFileRef = useRef<string | null>(fileId);
  const prevFileContentRef = useRef<string>('_init_');

  const [cursorPosition, setCursorPosition] = useState<{
    line: number | null;
    column: number | null;
  }>({ line: null, column: null });

  const [selection, setSelection] = useState<{
    startLine: number | null;
    startColumn: number | null;
    endLine: number | null;
    endColumn: number | null;
  } | null>(null);

  usePresenceHeartbeat({
    sessionId,
    currentFile: file.filePath,
    cursorLine: cursorPosition.line,
    cursorColumn: cursorPosition.column,
    selection,
  });

  const userInitials = currentUser.displayName
    .split(' ')
    .map((n) => n[0])
    .join('')
    .substring(0, 2)
    .toUpperCase();

  // Log + sync local content on file change
  useEffect(() => {
    console.debug('[CodeEditor] File changed:', file.fileName);
    setContent(file.content || '');
  }, [file.id, file.content, file.fileName, file.filePath]);

  // Flush pending debounced edits to previous file on file switch
  useEffect(() => {
    const currentId = fileId;

    if (prevFileRef.current !== currentId) {
      const prevId = prevFileRef.current;

      try {
        if (
          prevId != null &&
          lastEditFileIdRef.current === prevId &&
          typeof debouncedContent === 'string' &&
          debouncedContent !== prevFileContentRef.current &&
          debouncedContent.trim() !== ''
        ) {
          updateFile.mutate(
            { id: prevId, content: debouncedContent },
            { onError: () => toast.error('Failed to save changes') },
          );
        }
      } catch (e) {
        console.error('[CodeEditor] Failed flushing pending edit on file switch:', e);
      }

      lastEditFileIdRef.current = null;
      prevFileRef.current = currentId;
      prevFileContentRef.current = file.content ?? '';
    } else {
      prevFileContentRef.current = file.content ?? '';
    }
  }, [fileId, file.content, debouncedContent, updateFile]);

  // Auto-save when debounced content changes (for the current file only)
  useEffect(() => {
    // No actual change vs DB snapshot
    if (debouncedContent === file.content) return;

    // Guard against overwriting with empty string on initial load / weird mobile cases
    if ((file.content == null || file.content === undefined) && debouncedContent === '') {
      return;
    }

    // Only autosave if we know the last edit belonged to this file
    if (lastEditFileIdRef.current !== fileId) {
      return;
    }

    updateFile.mutate(
      { id: fileId, content: debouncedContent },
      {
        onError: () => toast.error('Failed to save changes'),
      },
    );

    lastEditFileIdRef.current = null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedContent, fileId, file.content]);

  const getLanguageFromFileName = (fileName: string): string => {
    const ext = fileName.split('.').pop()?.toLowerCase() || '';
    const extMap: Record<string, string> = {
      tsx: 'typescriptreact',
      jsx: 'javascriptreact',
      ts: 'typescript',
      js: 'javascript',
      mjs: 'javascript',
      cjs: 'javascript',
      py: 'python',
      rb: 'ruby',
      php: 'php',
      java: 'java',
      cs: 'csharp',
      cpp: 'cpp',
      cc: 'cpp',
      cxx: 'cpp',
      hpp: 'cpp',
      h: 'c',
      rs: 'rust',
      go: 'go',
      kt: 'kotlin',
      swift: 'swift',
      dart: 'dart',
      fs: 'fsharp',
      hs: 'haskell',
      clj: 'clojure',
      vue: 'vue',
      svelte: 'svelte',
      html: 'html',
      htm: 'html',
      css: 'css',
      scss: 'scss',
      sass: 'sass',
      json: 'json',
      yaml: 'yaml',
      yml: 'yaml',
      xml: 'xml',
      sql: 'sql',
      md: 'markdown',
      markdown: 'markdown',
      sh: 'shell',
      bash: 'shell',
      zsh: 'shell',
      ps1: 'powershell',
      dockerfile: 'dockerfile',
      gd: 'gdscript',
      tscn: 'gdscript',
      escn: 'gdscript',
    };
    return extMap[ext] || 'plaintext';
  };

  const handleEditorWillMount = (_monaco: any) => {
    // No-op for CodeMirror (kept for API symmetry / future Monaco)
  };

  const saveImmediatelyRef = useRef<() => void>(() => {});

  const saveImmediately = useCallback(() => {
    if (content !== file.content && content.trim() !== '') {
      lastEditFileIdRef.current = fileId;
      prevFileRef.current = fileId;
      prevFileContentRef.current = content;

      updateFile.mutate(
        { id: fileId, content },
        {
          onSuccess: () => toast.success('Saved'),
          onError: () => toast.error('Failed to save'),
        },
      );
    } else {
      toast.info('No changes to save');
    }
  }, [content, file.content, fileId, updateFile]);

  useEffect(() => {
    saveImmediatelyRef.current = saveImmediately;
  }, [saveImmediately]);

  // Global keyboard shortcuts
  useEffect(() => {
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        saveImmediatelyRef.current();
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 'f') {
        const target = e.target as HTMLElement;
        if (target.closest('[data-code-editor]') || target.closest('.cm-editor')) {
          e.preventDefault();
          searchInputRef.current?.focus();
          searchInputRef.current?.select();
        }
      }
    };

    document.addEventListener('keydown', handleGlobalKeyDown);
    return () => document.removeEventListener('keydown', handleGlobalKeyDown);
  }, []);

  const handleEditorDidMount = (editor: CodeMirrorEditorType, _monaco: any) => {
    editorRef.current = editor;

    const cursorDisposable = editor.onDidChangeCursorPosition?.((e: any) => {
      setCursorPosition({
        line: e.position.lineNumber,
        column: e.position.column,
      });
    });

    const selectionDisposable = editor.onDidChangeCursorSelection?.((e: any) => {
      const sel = e.selection;
      if (sel.startLineNumber !== sel.endLineNumber || sel.startColumn !== sel.endColumn) {
        setSelection({
          startLine: sel.startLineNumber,
          startColumn: sel.startColumn,
          endLine: sel.endLineNumber,
          endColumn: sel.endColumn,
        });
      } else {
        setSelection(null);
      }
    });

    return () => {
      cursorDisposable?.dispose?.();
      selectionDisposable?.dispose?.();
    };
  };

  useEffect(() => {
    const editor = editorRef.current as any;
    if (editor?.searchInFile) {
      editor.searchInFile(searchQuery);
    }
  }, [searchQuery]);

  return (
    <div className={styles.panel} data-code-editor>
      <div className={styles.toolbar}>
        <div className={styles.fileLabel}>
          <span className={styles.filePath}>{(file.filePath || '/').replace(/\/+$/, '')}/</span>
          <span className={styles.fileName}>{file.fileName}</span>
        </div>
        <div className={styles.searchBar}>
          <Search size={16} className={styles.searchIcon} />
          <Input
            ref={searchInputRef}
            id="code-editor-search"
            name="code-editor-search"
            type="text"
            placeholder="Search in file..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={styles.searchInput}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                setSearchQuery('');
              } else if (e.key === 'Enter') {
                e.preventDefault();
                const editor = editorRef.current as any;
                if (e.shiftKey) {
                  editor?.findPrevInFile?.();
                } else {
                  editor?.findNextInFile?.();
                }
              }
            }}
          />
        </div>

        {onAnnotate && (
          <div style={{ marginLeft: 8 }}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  onClick={() => {
                    const lines = (content || '').split('\n').slice(0, 200);
                    const padding = 16;
                    const lineHeight = 18;
                    const maxLineLength = Math.max(...lines.map((l) => l.length), 80);
                    const canvasWidth = Math.min(
                      1400,
                      Math.max(600, Math.round(maxLineLength * 8) + padding * 2),
                    );
                    const canvasHeight = padding * 2 + lineHeight * lines.length;
                    const canvas = document.createElement('canvas');
                    canvas.width = canvasWidth * window.devicePixelRatio;
                    canvas.height = canvasHeight * window.devicePixelRatio;
                    const ctx = canvas.getContext('2d');
                    if (ctx) {
                      ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
                      ctx.fillStyle = '#1e1e1e';
                      ctx.fillRect(0, 0, canvasWidth, canvasHeight);
                      ctx.font = '13px "JetBrains Mono", monospace';
                      ctx.fillStyle = '#ddd';
                      ctx.textBaseline = 'top';
                      for (let i = 0; i < lines.length; i++) {
                        ctx.fillText(lines[i], padding, padding + i * lineHeight);
                      }
                      const dataUrl = canvas.toDataURL('image/png');
                      onAnnotate(dataUrl);
                    }
                  }}
                >
                  <Pencil size={16} />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Annotate file view</TooltipContent>
            </Tooltip>
          </div>
        )}
      </div>

      <div className={styles.panel__body}>
        <Editor
          key={fileId}
          height="100%"
          path={`${file.filePath || '/'}${file.fileName || 'untitled.txt'}`.replace(/\/+/g, '/')}
          defaultLanguage={file.language || getLanguageFromFileName(file.fileName) || 'plaintext'}
          language={file.language || getLanguageFromFileName(file.fileName) || 'plaintext'}
          value={content || ''}
          onChange={(value) => {
            setContent(value || '');
            lastEditFileIdRef.current = fileId;
          }}
          theme={monacoTheme}
          extraExtensions={lspExtensions}
          beforeMount={handleEditorWillMount}
          onMount={handleEditorDidMount}
          loading={
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                color: 'var(--muted-foreground)',
                fontFamily: 'var(--font-family-base)',
              }}
            >
              Loading editor...
            </div>
          }
          options={{
            minimap: { enabled: false },
            fontSize: 13,
            fontFamily: "'JetBrains Mono', 'Fira Code', 'Consolas', monospace",
            scrollBeyondLastLine: false,
            padding: { top: 10, bottom: 10 },
            lineNumbers: 'on',
            wordWrap: 'on',
            tabSize: 2,
            insertSpaces: true,
            enableAutocomplete: (content?.length || 0) < 100000,
            enableLinting: false,
          }}
        />
        <CollaboratorCursors
          sessionId={sessionId}
          currentFile={file.filePath}
          currentUserId={currentUser.id}
          editorRef={editorRef}
        />
      </div>

      <div className={styles.panel__footer}>
        <div className={styles.statusBar}>
          <div className={styles.leftStatus}>
            <div className={styles.userStatus}>
              <UserAvatar user={currentUser} size="xs" showStatus={true} />
              <span className={styles.userName}>{currentUser.displayName}</span>
            </div>
          </div>

          <div className={styles.rightStatus}>
            <span>{file.language}</span>
            <span className={styles.status}>{updateFile.isPending ? 'Saving...' : 'Saved'}</span>
          </div>
        </div>
      </div>
    </div>
  );
};
